import {
    ScheduleEntry,
    ScheduleVesselAvailability,
    SimplifiedPortCall
} from '../models/types/voyage-schedules-model.types';
import {authFetchDELETE, authFetchGET, authFetchPOST, authFetchPUT, FetchStatusCodeError} from '../utils/auth-fetch';
import {VesselTEUResponse} from "../models/types/vessels-model.types";

const env = (<any>window)["appenv"].services.schedulesapi;

interface ScheduledVoyageLeg {
    id: string;
    departureTerminalCode: string;
    departureTerminalUnLocode: string;
    departureAt: string;
    arrivalTerminalCode: string;
    arrivalTerminalUnLocode: string;
    arrivalAt: string;
    arrivalPortCall?: {
        GSIS: SimplifiedPortCall | null;
    };
    departurePortCall?: {
        GSIS: SimplifiedPortCall | null;
    };
}

interface PortCall {
    id: number;
    submittedAt: string;
    imo: number;
    terminalCode: string;
    terminalUnLocode: string;
    arrivalAt: string;
    departureAt: string;
    arrivalDirection: string;
    serviceCode?: string; 
    operatorCode?: string
}

interface VesselStatus {
    status: string;
    voyage: ScheduledVoyageLeg;
    portCall: PortCall;
}

export interface PortCallChange {
    id?: number;
    gsisId: number | null;
    createDtm?: string;
    terminalCode: string;
    arrivalAt: string;
    departureAt: string;
    changeType?: "add" | "change" | "delete";
}

export const SchedulesService = new class extends EventTarget {

    private scheduleVesselData: any;

    async fetchScheduleVesselAvailability(gapThreshold?: number): Promise<{ [index: string]: ScheduleVesselAvailability }> {
        let options: { [index: string]: string } = {}

        if (gapThreshold) {
            options.gapThreshold = `${gapThreshold}`
        }

        return await authFetchGET(env.scope, `${env.baseurl}/vessel-availability`, options);
    }

    async fetchScheduleDetailsForVessel(imoNo: number, includeNumBefore = 0, fromDate = new Date()): Promise<{ [index: number]: ScheduleEntry[] }> {
        let schedulesPromise = this._fetchSchedulesForVessel(imoNo, includeNumBefore, fromDate.toISOString());
        let vesselStatusPromise = this.fetchScheduleVesselStates(imoNo);

        let schedules = await schedulesPromise;
        const vesselStatus = await vesselStatusPromise;

        if (vesselStatus.length === 0 || !schedules[imoNo]) {
            return schedules;
        }

        schedules[imoNo].forEach(item => {
            item.active = item.etd === vesselStatus[0].schedule.startTime || item.etd === vesselStatus[0].schedule.endTime;
            item.inPort = item.active && vesselStatus[0].schedule.inPort;
        });

        return schedules;
    }

    async fetchVesselSchedule(imoNo: number | null = null) {

            let options: { [index: string]: string } = {
                onlyMaerskOperated: "false"
            };
            if (imoNo) {
                options.imo = `${imoNo}`;
            }
            const data = await authFetchGET(env.scope, `${env.baseurl}/vesselstatus`, options);
            return data;
       
    }

    async fetchScheduleVesselStates(imoNo: number | null = null) {
        let results = [];

        if(this.scheduleVesselData && !imoNo){
            return this.scheduleVesselData;
        }

        try {
            let options: { [index: string]: string } = {
                onlyMaerskOperated: "false"
            };
            if (imoNo !== null) {
                options.imo = `${imoNo}`;
            }
            const data = await authFetchGET(env.scope, `${env.baseurl}/vesselstatus`, options);

        for (let imo in data) {
                const item = this._mapScheduleVesselStates((data[imo].length ? data[imo][0] : data[imo]), imo);
                if (item) {
                    results.push(item);
                }
            }
        }
        catch (err) {
            // Return empty array when no schedule is available (ie. 404 error)
            if (err instanceof FetchStatusCodeError) {
                if (err.response.status === 404) {
                    return [];
                }
            }
            throw err;
        }
        if(!imoNo){
            this.scheduleVesselData = results;
        }
        return results;
    }

    // Gets a TEU for a specific vessel.
    async getVesselTEU(imoNo?: number): Promise<VesselTEUResponse[]|null> {
        if(imoNo){
            return await authFetchGET(env.scope, `${env.baseurl}/vessels/${imoNo}`)
        }
        return null
    }

    async changePortCall(change: PortCallChange): Promise<PortCallChange> {
        let url: string;
        if (change.gsisId) {
            url = `${env.baseurl}/portcalls/gsis/${change.gsisId}`;
        }
        else {
            if (!change.id) {
                throw new Error("Neither GSIS nor local id specified");
            }
            url = `${env.baseurl}/portcalls/local/${change.id}`;
        }

        return await authFetchPUT(env.scope, url, change);
    }

    async deletePortCall(gsisId: number | null, localId: number | null): Promise<PortCallChange | undefined> {
        let url: string;
        if (gsisId) {
            url = `${env.baseurl}/portcalls/gsis/${gsisId}`;
        }
        else {
            if (!localId) {
                throw new Error("Neither GSIS nor local id specified");
            }
            url = `${env.baseurl}/portcalls/local/${localId}`;
        }

        return await authFetchDELETE(env.scope, url);
    }

    async addPortCall(newPortCall: PortCallChange): Promise<PortCallChange> {
        return await authFetchPOST(env.scope, `${env.baseurl}/portcalls/local`, newPortCall);
    }

    private async _fetchSchedulesForVessel(imoNo: number, includeNumBefore: number, fromDate: string) {
        const data: { [index: number]: ScheduleEntry[] } = await authFetchGET(env.scope, `${env.baseurl}/voyagelegs`,
            { imo: `${imoNo}`, b: `${includeNumBefore}`, t: fromDate });

        if (data[imoNo]) {
            data[imoNo].forEach((item: ScheduleEntry) => {
                item.etd = (new Date(item.departureAt)).getTime();
                item.eta = (new Date(item.arrivalAt)).getTime();
                item.progress = Math.ceil(Math.max(0, ((new Date().getTime() - item.etd) * 100) / (item.eta - item.etd)));
            });
        }

        return data;
    }

    private _mapScheduleVesselStates(vesselStatus: VesselStatus, imo: string){ 
        switch(vesselStatus.status) {
            case 'on-voyage':
                return {
                    imoNo: Number.parseInt(imo),
                    schedule: {
                        startPort: vesselStatus.voyage.departureTerminalCode,
                        endPort: vesselStatus.voyage.arrivalTerminalCode,
                        startTime: (new Date(vesselStatus.voyage.departureAt)).getTime(),
                        endTime: (new Date(vesselStatus.voyage.arrivalAt)).getTime(),
                        inPort: false,
                        serviceCode: vesselStatus.voyage.departurePortCall?.GSIS?.serviceCode,
                        operatorCode: vesselStatus.voyage.departurePortCall?.GSIS?.operatorCode ?? vesselStatus.voyage.arrivalPortCall?.GSIS?.operatorCode,
                        id:vesselStatus.voyage.id
                    },
                };
            case 'in-port':
                return {
                    imoNo: Number.parseInt(imo),
                    schedule: {
                        startPort: vesselStatus.portCall.terminalCode,
                        endPort: vesselStatus.portCall.terminalCode,
                        startTime: (new Date(vesselStatus.portCall.arrivalAt)).getTime(),
                        endTime: (new Date(vesselStatus.portCall.departureAt)).getTime(),
                        inPort: true,
                        serviceCode: vesselStatus.portCall.serviceCode,
                        operatorCode: vesselStatus.portCall.operatorCode,
                        id:""
                    },
                };
            default:
                return null;
        }
    }
}